/**
 * Inline-filter reconciliation — one implementation, two call sites.
 *
 * THE RULE (owner-spec, restated 2026-08-30)
 *   Every serialized device on an opportunity needs one "סנן אינליין" slot.
 *   Every system sold cancels one, because the system already contains a
 *   filter. So:
 *
 *       desired = max(0, Σ serialized qty − Σ system qty)
 *
 *   and the reconciliation moves the actual inline-filter quantity to
 *   `desired`. Not "add one when a device is added" — the whole opportunity
 *   is recomputed after every change.
 *
 * WHY IT IS A RECONCILIATION AND NOT A DELTA ON THE BATCH
 *   The old code asked "does THIS batch contain a system?" and added a filter
 *   when it did not. Two owner-reported bugs fell straight out of that:
 *     - delete a serial device, add a different one -> a SECOND filter, because
 *       the batch could not see the filter that already existed
 *     - add a system in a LATER action -> the filter stayed, because the batch
 *       check had already passed
 *   Both disappear when the question is "what should the total be, given the
 *   opportunity as it now stands".
 *
 * WHY EVERYTHING IS KEYED ON PricebookEntryId
 *   `opportunitylineitem` has 22 columns and NONE of them is Product2Id. The
 *   only link a line item has to a product is `PricebookEntryId`. The
 *   `Product2__Name` / `Product2__IsSerialized` fields that the previous
 *   implementation read are generated at READ TIME by the reference resolver
 *   (unified_server.py:14880) and are therefore absent from every
 *   `resolve_refs=false` response — which is what both call sites use, and why
 *   both of the previous reconciliation blocks were silently dead code.
 *
 * WHY CLASSIFICATION COMES FROM DATA
 *   Product2.Is_System__c, not `Name.includes('מערכת')`. Against the live
 *   catalogue that substring missed 152 rows of osmosis systems and wrongly
 *   caught 213 rows of accessories — a faucet named "ברז למערכת תת־כיורית"
 *   suppressed a filter. See migration 2026-08-30_product2_is_system.sql.
 */

/** The one inline-filter product. Pinned by Id: the catalogue holds a second
 *  row with the identical name (`01tbV00000QUErNQAX`, zero usage), so
 *  `find(name.includes(...))` picked whichever the pricebook happened to list
 *  first. */
import { authHeaders, deleteHeaders, DELETE_AUTOMATIC } from './session';

import { fetchMarketerContext } from './marketer';

export const INLINE_FILTER_PRODUCT_ID = '01tHp00000BWR8XIAX';

/** SF-style booleans arrive as TEXT '0'/'1' on this database, but the platform
 *  query layer and the pandas cache have both been seen to hand back real
 *  booleans and numbers. Accept all three rather than trusting one. */
function isTrue(v) {
  return v === true || v === 1 || v === '1' || v === 'true' || v === 'TRUE' || v === 'True';
}

function qtyOf(li) {
  const n = parseInt(li && li.Quantity, 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Index a loaded product list by PricebookEntryId.
 *
 * `products` is the array both call sites already hold in memory — the one
 * ProductsModal builds from the pricebook and the one App.js loads for the
 * line-item edit modal. No extra request, and in particular no
 * `resolve_refs=true`, whose N+1 was deliberately removed in PR #772.
 *
 * `sawSystemField` reports whether ANY product carried an `Is_System__c` key.
 * The platform query layer drops unknown columns silently
 * (csv_data_layer.py:421), so before the migration runs the field is simply
 * absent — every product would look like "not a system", every filter would
 * look uncancelled, and the reconciliation would happily add filters that the
 * systems on the opportunity should have cancelled. That is worse than doing
 * nothing, so callers must refuse to act on it. See `computeInlineFilterPlan`.
 */
export function buildProductIndex(products) {
  const byPbe = {};
  const byProductId = {};
  let sawSystemField = false;

  for (const p of products || []) {
    if (!p || !p.pricebookEntryId) continue;
    if (p.Is_System__c !== undefined) sawSystemField = true;

    const entry = {
      id: p.Id,
      name: p.Name || '',
      pricebookEntryId: p.pricebookEntryId,
      unitPrice: p.unitPrice,
      // `isSerialized` is precomputed by both call sites; fall back to the raw
      // column for any caller that passes products straight from the API.
      isSerialized: p.isSerialized !== undefined ? !!p.isSerialized : isTrue(p.IsSerialized),
      isSystem: isTrue(p.Is_System__c),
    };
    byPbe[p.pricebookEntryId] = entry;
    byProductId[p.Id] = entry;
  }

  return { byPbe, byProductId, sawSystemField };
}

/**
 * Decide what the inline-filter quantity should be, or refuse.
 *
 * FAIL-CLOSED, and deliberately so. A partial count cannot produce a partial
 * answer here — it produces a WRONG one, and the wrong answer deletes a filter
 * that should exist. If a single line item cannot be resolved to a product, its
 * serialized quantity is missing from the sum, `desired` comes out too low, and
 * the caller would delete real rows on the strength of data it never had.
 *
 * So: every line item resolves and the classification is present, or the plan
 * is `ok: false` and the caller writes nothing. An opportunity left
 * unreconciled is recoverable; a filter deleted on incomplete information is
 * not obviously wrong to anyone looking at it later.
 */
export function computeInlineFilterPlan(lineItems, index) {
  if (!index || !index.sawSystemField) {
    return { ok: false, reason: 'classification-unavailable' };
  }

  const filterRows = [];
  let serialQty = 0;
  let systemQty = 0;
  let currentQty = 0;

  for (const li of lineItems || []) {
    const product = index.byPbe[li && li.PricebookEntryId];
    if (!product) {
      return {
        ok: false,
        reason: 'unresolved-line-item',
        lineItemId: (li && li.Id) || null,
        pricebookEntryId: (li && li.PricebookEntryId) || null,
      };
    }

    const qty = qtyOf(li);

    // The filter itself is neither a device nor a system — it is the thing
    // being counted.
    if (product.id === INLINE_FILTER_PRODUCT_ID) {
      currentQty += qty;
      filterRows.push({ id: li.Id, qty, createdDate: li.CreatedDate || '' });
      continue;
    }

    if (product.isSerialized) serialQty += qty;
    // Not `else if`: a product could in principle be both, and the rule is
    // about what it needs and what it cancels, not about which bucket it is in.
    if (product.isSystem) systemQty += qty;
  }

  const desiredQty = Math.max(0, serialQty - systemQty);

  // Oldest first: when quantity has to come off, take it from the line the
  // rep has been looking at longest, and leave the most recent row intact.
  filterRows.sort((a, b) => String(a.createdDate).localeCompare(String(b.createdDate)));

  return {
    ok: true,
    serialQty,
    systemQty,
    currentQty,
    desiredQty,
    delta: desiredQty - currentQty,
    filterRows,
  };
}

/**
 * Fetch the opportunity's line items, decide, and write the difference.
 *
 * Call this AFTER the user's own change has landed — adding products, editing a
 * line, deleting a line. The user's action is never gated on this: if the plan
 * refuses, their change still stands and only the filter is left alone.
 *
 * `resolve_refs=false` is kept deliberately. The resolver's Product2 leg costs
 * 300-700ms of N+1 (PR #772) and this function does not need it — the product
 * index is already in memory and `PricebookEntryId` is on every row.
 *
 * Returns `{ changed, plan, applied }`. `changed` is false both when nothing
 * needed doing and when the plan refused; `plan.ok` distinguishes them.
 */
export async function reconcileInlineFilter({
  apiBase,
  opportunityId,
  products,
  actor = {},
  fetchImpl = fetch,
  log = console,
  isMarketerSale = null,
}) {
  if (!opportunityId) return { changed: false, plan: { ok: false, reason: 'no-opportunity' } };

  // 2026-10-09 (owner): a marketer sale gets NO automatic inline filter — not added, not resized,
  // not removed. Checked here so every caller (products window, line edit, line delete) obeys it.
  // An unreachable check means "not a marketer sale", i.e. today's behaviour; the server's price
  // guard still refuses a 0-priced line on a marketer opportunity.
  const marketer = await (isMarketerSale
    ? isMarketerSale(opportunityId)
    : fetchMarketerContext(opportunityId, fetchImpl).then(c => c.active));
  if (marketer) {
    log.log('[inline-filter] marketer sale — no automatic inline filter');
    return { changed: false, plan: { ok: false, reason: 'marketer-sale' } };
  }

  const index = buildProductIndex(products);

  const relatedRes = await fetchImpl(
    `${apiBase}/api/objects/Opportunity/records/${opportunityId}/related` +
    `?limit=1000&resolve_refs=false&_t=${Date.now()}`
  );
  if (!relatedRes.ok) {
    log.warn('[inline-filter] related fetch failed; leaving the filter alone');
    return { changed: false, plan: { ok: false, reason: 'related-fetch-failed' } };
  }
  const related = await relatedRes.json();
  const lineItems = (related.related_objects
    && related.related_objects.OpportunityLineItem
    && related.related_objects.OpportunityLineItem.records) || [];

  const plan = computeInlineFilterPlan(lineItems, index);

  if (!plan.ok) {
    // Loud on purpose. Silence here reads as "reconciled, nothing to do", which
    // is the one conclusion that is definitely wrong.
    log.warn(
      `[inline-filter] REFUSED (${plan.reason}) — no writes. ` +
      (plan.reason === 'unresolved-line-item'
        ? `line item ${plan.lineItemId} has PricebookEntryId ${plan.pricebookEntryId}, ` +
          'which is not in the loaded pricebook.'
        : 'Product2.Is_System__c is absent from the product payload — the ' +
          'migration has probably not run yet.')
    );
    return { changed: false, plan };
  }

  if (plan.delta === 0) return { changed: false, plan, applied: [] };

  log.log(
    `[inline-filter] serial=${plan.serialQty} − system=${plan.systemQty} ` +
    `= desired ${plan.desiredQty} (current ${plan.currentQty}, delta ${plan.delta})`
  );

  const applied = [];
  const oliUrl = `${apiBase}/api/objects/OpportunityLineItem/records`;
  const now = new Date().toISOString();

  if (plan.delta > 0) {
    const existing = plan.filterRows[0];
    if (existing) {
      // Grow the row that is already there rather than opening a second one.
      // Two filter rows on one opportunity is what the old batch logic produced
      // and what the rep then had to reconcile by hand.
      const newQty = existing.qty + plan.delta;
      await fetchImpl(`${oliUrl}/${existing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ Quantity: newQty, TotalPrice: 0 }),
      });
      applied.push({ op: 'update', id: existing.id, from: existing.qty, to: newQty });
    } else {
      const filterProduct = index.byProductId[INLINE_FILTER_PRODUCT_ID];
      if (!filterProduct) {
        log.warn('[inline-filter] the filter product is not in this pricebook — cannot add');
        return { changed: false, plan: { ...plan, ok: false, reason: 'filter-not-in-pricebook' } };
      }
      // Price 0, always. The filter is included with the device; it is a
      // fulfilment line, not a charge, and the opportunity total must not move.
      await fetchImpl(oliUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          OpportunityId: opportunityId,
          PricebookEntryId: filterProduct.pricebookEntryId,
          Product2Id: filterProduct.id,
          Product2__Name: filterProduct.name,
          Name: filterProduct.name,
          ProductName: filterProduct.name,
          Description: filterProduct.name,
          Quantity: plan.desiredQty,
          UnitPrice: 0,
          TotalPrice: 0,
          Device_At_Customer__c: '0',
          CreatedById: actor.id || '',
          CreatedByName: actor.name || '',
          LastModifiedById: actor.id || '',
          LastModifiedByName: actor.name || '',
          OwnerId: actor.id || '',
          Owner__Name: actor.name || '',
          CreatedDate: now,
          LastModifiedDate: now,
        }),
      });
      applied.push({ op: 'insert', qty: plan.desiredQty });
    }
  } else {
    // Take the surplus off oldest-first; a row that reaches zero is removed
    // rather than left as a 0-quantity line nobody can explain.
    let toRemove = -plan.delta;
    for (const row of plan.filterRows) {
      if (toRemove <= 0) break;
      if (row.qty <= toRemove) {
        // 2026-08-30: the token travels here too. A filter row removed by the
        // reconciliation is still traceable to the person whose action set it
        // off — the 05:50:19 deletion that recorded 'unknown' came from this
        // line. Marking the event as AUTOMATIC rather than manual is a
        // separate concern and deliberately not solved here.
        await fetchImpl(`${oliUrl}/${row.id}`, {
          method: 'DELETE',
          headers: deleteHeaders('OLI_DELETE_RECONCILE', DELETE_AUTOMATIC),
        });
        applied.push({ op: 'delete', id: row.id, qty: row.qty });
        toRemove -= row.qty;
      } else {
        const newQty = row.qty - toRemove;
        await fetchImpl(`${oliUrl}/${row.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ Quantity: newQty, TotalPrice: 0 }),
        });
        applied.push({ op: 'update', id: row.id, from: row.qty, to: newQty });
        toRemove = 0;
      }
    }
  }

  return { changed: applied.length > 0, plan, applied };
}
